export type SiteLookupDataView = 'historical' | 'realtime';

export const SiteLookupDataViewValue = {
  historical: 'historical' as SiteLookupDataView,
  realtime: 'realtime' as SiteLookupDataView,
};

type GetAvailableSiteLookupDataViewsProps = {
  hasHistoricalAccess: boolean;
  hasRealTimeAccess: boolean;
};

export const getAvailableSiteLookupDataViews = ({ hasHistoricalAccess, hasRealTimeAccess }: GetAvailableSiteLookupDataViewsProps): SiteLookupDataView[] => {
  const availableDataViews: SiteLookupDataView[] = [];

  if (hasHistoricalAccess) {
    availableDataViews.push(SiteLookupDataViewValue.historical);
  }

  if (hasRealTimeAccess) {
    availableDataViews.push(SiteLookupDataViewValue.realtime);
  }

  return availableDataViews;
};

/** The preferred view is remembered per user on this device. */
export const dataViewStorageKey = (userId: string) => `siteLookup.dataView.${userId}`;

const isDataView = (value: unknown): value is SiteLookupDataView => value === SiteLookupDataViewValue.historical || value === SiteLookupDataViewValue.realtime;

export const readStoredDataView = (userId: string | undefined): SiteLookupDataView | null => {
  if (!userId) return null;
  try {
    const stored = window.localStorage.getItem(dataViewStorageKey(userId));
    return isDataView(stored) ? stored : null;
  } catch {
    // Storage may be unavailable (private mode, policies): the default view is used.
    return null;
  }
};

export const storeDataView = (userId: string | undefined, view: SiteLookupDataView) => {
  if (!userId) return;
  try {
    window.localStorage.setItem(dataViewStorageKey(userId), view);
  } catch {
    // Not remembering the choice is acceptable.
  }
};

/** The preferred view when it is permitted, otherwise the first permitted one. */
export const resolveDataView = (preferred: SiteLookupDataView | null, availableDataViews: SiteLookupDataView[]): SiteLookupDataView => {
  if (preferred && availableDataViews.includes(preferred)) {
    return preferred;
  }

  return availableDataViews[0] || SiteLookupDataViewValue.realtime;
};
